/**
 * ImportGuidePage — vehicle-import conditions reference (Phase 4 Stream J).
 *
 * Migrated from PSM `portal/src/pages/ImportGuidePage.tsx` (561 LOC, static
 * educational content). Phase 1 audit found the page existed in PSM portal
 * but was NEVER registered in PSM `portal/src/App.tsx` — a "ghost page".
 * Customer-portal explicitly wires it at `/guide/import` and adds the
 * sidebar entry so the content is actually reachable.
 *
 * Adaptations vs PSM source:
 *   - PSM source uses Tailwind + lucide-react + a separate
 *     `useIsMobile()` desktop/mobile split. Customer-portal/web has no
 *     Tailwind dependency, so this port renders a single layout using
 *     inline styles + unicode glyphs and a CSS-grid `repeat(auto-fit,
 *     minmax(...))` to collapse to a single column on narrow viewports
 *     instead of branching on a JS-detected breakpoint.
 *   - All content preserved verbatim: accuracy summary (4 items), 5-layer
 *     import-condition diagram, 4 country notes with official links,
 *     4 common blind spots, 4 recommended actions, disclaimer.
 *   - Country notes and blind spots remain collapsible (accordion).
 *   - Anchor links allow jump-to-section navigation.
 *   - No API calls — the page is pure static content.
 *
 * Spec:
 *   - docs/specs/PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.2 Phase 4 Stream J
 *   - docs/specs/PORTAL_SEPARATION_INVENTORY.md (ghost-page note)
 */
import { useState } from 'react';
import { Card, CardBody } from '../components/ui';

interface AccuracyItem {
  label: string;
  range: string;
  level: 'good' | 'ok' | 'warn';
}

const accuracySummary: readonly AccuracyItem[] = [
  { label: 'General guidance', range: '70–90%', level: 'good' },
  { label: 'Country-level overview', range: '60–85%', level: 'ok' },
  { label: 'Direct use for customs, registration & tax decisions', range: '30–70%', level: 'warn' },
  { label: 'Verified against official sources', range: '80–95%', level: 'good' },
];

interface ImportLayer {
  glyph: string;
  label: string;
  desc: string;
}

const importLayers: readonly ImportLayer[] = [
  { glyph: '🚚', label: 'Customs clearance', desc: 'Import declaration & duty procedures' },
  { glyph: '⚖️', label: 'Taxes', desc: 'Customs duty, VAT/GST & registration tax' },
  { glyph: '🔍', label: 'Vehicle inspection', desc: 'Safety standards & emissions testing' },
  { glyph: '📄', label: 'Registration', desc: 'Vehicle registration & license plates' },
  { glyph: '🛡️', label: 'Country-specific rules', desc: 'Age limits, biosecurity & more' },
];

interface CountryNote {
  country: string;
  flag: string;
  points: string[];
  officialUrl: string;
  officialLabel: string;
}

const countryNotes: readonly CountryNote[] = [
  {
    country: 'United Kingdom',
    flag: '🇬🇧',
    points: [
      'Import notification to HMRC, tax payment, vehicle approval (IVA) and DVLA registration are required as separate steps',
      'Right-hand-drive vehicles can be registered as-is, but left-hand-drive vehicles may require additional inspection',
      'Customs clearance, taxes, vehicle approval and registration are separate stages',
    ],
    officialUrl: 'https://www.gov.uk/importing-vehicles-into-the-uk',
    officialLabel: 'GOV.UK',
  },
  {
    country: 'Australia',
    flag: '🇦🇺',
    points: [
      'Prior approval through the ROVER system is required before import',
      'Framework based on the RVS legislation',
      'A separate biosecurity inspection (proof of vehicle cleaning) is required',
    ],
    officialUrl:
      'https://www.infrastructure.gov.au/infrastructure-transport-vehicles/vehicles/importing-road-vehicle-australia',
    officialLabel: 'Infrastructure.gov.au',
  },
  {
    country: 'New Zealand',
    flag: '🇳🇿',
    points: [
      'Border inspection and entry certification are handled separately',
      'Possible additional charges under the Clean Car Programme',
      'Strict biosecurity requirements',
    ],
    officialUrl: 'https://www.nzta.govt.nz/vehicles/importing-a-vehicle',
    officialLabel: 'NZ Transport Agency',
  },
  {
    country: 'Ireland',
    flag: '🇮🇪',
    points: [
      'Vehicles from outside the EU incur customs duty + VAT + VRT (Vehicle Registration Tax)',
      'VRT is calculated based on the vehicle’s OMV (Open Market Value)',
      'Tax amounts are easily overlooked when looking only at “import conditions”',
    ],
    officialUrl: 'https://www.revenue.ie/en/vrt/calculating-vrt/vat-customs-duty.aspx',
    officialLabel: 'Revenue.ie',
  },
];

interface BlindSpot {
  title: string;
  desc: string;
}

const blindSpots: readonly BlindSpot[] = [
  {
    title: 'Conditions don’t fit on a single page',
    desc: 'In the UK, importing requires HMRC notification, tax payment, vehicle approval and DVLA registration afterwards. There is no single answer for “import conditions.”',
  },
  {
    title: '“Exceptions” change the answer',
    desc: 'Results differ depending on temporary vs. permanent import, individual vs. business, new vs. used vehicle, right- vs. left-hand drive, and resident vs. non-resident.',
  },
  {
    title: 'Identically named documents serve different purposes',
    desc: 'Whether an Invoice, B/L or Export Certificate is for customs, registration or port collection varies by country and procedure.',
  },
  {
    title: 'Regulations get updated',
    desc: 'Tax rates, inspection standards and required documents are revised periodically. Recording the date you checked is important.',
  },
];

interface Recommendation {
  rank: number;
  title: string;
  desc: string;
  badge: string;
}

const recommendations: readonly Recommendation[] = [
  {
    rank: 1,
    title: 'AI → organize the issues → verify officially → finalize for practice',
    desc: 'Treat AI as the first-pass answer. Make the final call on official sites. Consult a customs broker or local agent as needed.',
    badge: 'Operating rule',
  },
  {
    rank: 2,
    title: 'Break the question down before checking',
    desc: 'Check separately: import declaration documents / types of taxes / year, model & environmental rules / inspection requirements / post-arrival registration / individual vs. commercial differences.',
    badge: 'Higher accuracy',
  },
  {
    rank: 3,
    title: 'Distinguish “confirmed” from “reference” information',
    desc: 'Items with an official source and a verification date are confirmed. General guidance that needs rechecking is reference only. Reliability differs.',
    badge: 'Display design',
  },
  {
    rank: 4,
    title: 'Always verify tax rates, eligibility & required documents officially',
    desc: 'Make official verification mandatory before any sales or customer guidance — especially for tax rates, age limits and inspection requirements.',
    badge: 'Safeguard',
  },
];

interface SectionAnchor {
  id: string;
  label: string;
}

const SECTION_ANCHORS: readonly SectionAnchor[] = [
  { id: 'accuracy', label: 'AI accuracy guide' },
  { id: 'layers', label: 'The 5 layers' },
  { id: 'countries', label: 'Key destinations' },
  { id: 'blind-spots', label: 'Common blind spots' },
  { id: 'recommendations', label: 'Recommended actions' },
  { id: 'disclaimer', label: 'Disclaimer' },
];

function accuracyColors(level: AccuracyItem['level']): {
  border: string;
  background: string;
  rangeColor: string;
} {
  switch (level) {
    case 'good':
      return { border: '#a7f3d0', background: '#ecfdf5', rangeColor: '#047857' };
    case 'ok':
      return { border: '#fde68a', background: '#fffbeb', rangeColor: '#b45309' };
    case 'warn':
      return { border: '#fecaca', background: '#fef2f2', rangeColor: '#b91c1c' };
  }
}

export function ImportGuidePage(): JSX.Element {
  const [openCountry, setOpenCountry] = useState<number | null>(null);
  const [openBlind, setOpenBlind] = useState<number | null>(null);

  return (
    <section data-testid="import-guide-page" style={{ display: 'grid', gap: '1.5rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>
          <span aria-hidden="true">📘 </span>
          Import Conditions Guide
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
          An overview of each country’s vehicle-import conditions, plus tips for using AI-generated
          information responsibly.
        </p>
      </header>

      {/* Section anchors (in-page navigation). */}
      <nav
        aria-label="Sections on this page"
        data-testid="import-guide-anchors"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          padding: '0.75rem',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
        }}
      >
        {SECTION_ANCHORS.map((anchor) => (
          <a
            key={anchor.id}
            href={`#${anchor.id}`}
            style={{
              fontSize: '0.8125rem',
              color: '#1f4ed8',
              textDecoration: 'none',
              padding: '0.25rem 0.625rem',
              borderRadius: '9999px',
              background: '#eff6ff',
            }}
          >
            {anchor.label}
          </a>
        ))}
      </nav>

      {/* Accuracy overview. */}
      <Card aria-label="AI accuracy guide">
        <CardBody style={{ display: 'grid', gap: '0.75rem' }}>
          <h2
            id="accuracy"
            style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: '#111827' }}
          >
            <span aria-hidden="true">ℹ️ </span>
            AI accuracy guide
          </h2>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
            These are confidence ranges for answers based on AI knowledge alone. Cross-checking
            against official sources improves accuracy significantly.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
              gap: '0.75rem',
            }}
          >
            {accuracySummary.map((item) => {
              const colors = accuracyColors(item.level);
              return (
                <div
                  key={item.label}
                  style={{
                    border: `1px solid ${colors.border}`,
                    background: colors.background,
                    borderRadius: '0.5rem',
                    padding: '1rem',
                  }}
                >
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#1f2937' }}>
                    {item.label}
                  </p>
                  <p
                    style={{
                      margin: '0.25rem 0 0',
                      fontSize: '1.125rem',
                      fontWeight: 700,
                      color: colors.rangeColor,
                    }}
                  >
                    {item.range}
                  </p>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* 5 layers. */}
      <Card aria-label="The 5 layers of import conditions">
        <CardBody style={{ display: 'grid', gap: '0.75rem' }}>
          <h2
            id="layers"
            style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: '#111827' }}
          >
            <span aria-hidden="true">🛡️ </span>
            The 5 layers of import conditions
          </h2>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
            “Import conditions” actually consist of five independent procedures. Overlooking even
            one causes problems in practice.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
              gap: '0.75rem',
            }}
          >
            {importLayers.map((layer) => (
              <div
                key={layer.label}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  background: '#f9fafb',
                  borderRadius: '0.5rem',
                  padding: '1rem 0.75rem',
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '2.5rem',
                    height: '2.5rem',
                    borderRadius: '9999px',
                    background: 'rgba(31, 78, 216, 0.1)',
                    fontSize: '1.25rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  {layer.glyph}
                </div>
                <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' }}>
                  {layer.label}
                </p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                  {layer.desc}
                </p>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Country notes. */}
      <section
        aria-label="Notes for key destination countries"
        style={{ display: 'grid', gap: '0.75rem' }}
      >
        <h2
          id="countries"
          style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: '#111827' }}
        >
          <span aria-hidden="true">🌐 </span>
          Notes for key destination countries
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))',
            gap: '0.75rem',
          }}
        >
          {countryNotes.map((c, i) => {
            const isOpen = openCountry === i;
            return (
              <Card key={c.country}>
                <button
                  type="button"
                  onClick={() => setOpenCountry(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  aria-controls={`country-panel-${i}`}
                  data-testid={`country-toggle-${i}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    padding: '1rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span aria-hidden="true" style={{ fontSize: '1.5rem' }}>
                      {c.flag}
                    </span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' }}>
                      {c.country}
                    </span>
                  </span>
                  <span aria-hidden="true" style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    {isOpen ? '▲' : '▼'}
                  </span>
                </button>
                {isOpen ? (
                  <div
                    id={`country-panel-${i}`}
                    style={{
                      padding: '0.75rem 1rem 1rem',
                      borderTop: '1px solid #f3f4f6',
                      display: 'grid',
                      gap: '0.5rem',
                    }}
                  >
                    <ul
                      style={{
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                        display: 'grid',
                        gap: '0.375rem',
                      }}
                    >
                      {c.points.map((p, j) => (
                        <li
                          key={j}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem',
                            fontSize: '0.875rem',
                            color: '#4b5563',
                          }}
                        >
                          <span aria-hidden="true" style={{ color: '#10b981', flexShrink: 0 }}>
                            ✓
                          </span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href={c.officialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.75rem',
                        color: '#1f4ed8',
                        textDecoration: 'none',
                      }}
                    >
                      <span aria-hidden="true">🔗</span>
                      {c.officialLabel} (official)
                    </a>
                  </div>
                ) : null}
              </Card>
            );
          })}
        </div>
      </section>

      {/* Blind spots & Recommendations side-by-side on wide viewports. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))',
          gap: '1rem',
        }}
      >
        <Card aria-label="Common blind spots">
          <CardBody style={{ display: 'grid', gap: '0.75rem' }}>
            <h2
              id="blind-spots"
              style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: '#111827' }}
            >
              <span aria-hidden="true">⚠️ </span>
              Common blind spots
            </h2>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {blindSpots.map((b, i) => {
                const isOpen = openBlind === i;
                return (
                  <div
                    key={i}
                    style={{
                      border: '1px solid #f3f4f6',
                      borderRadius: '0.5rem',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenBlind(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      aria-controls={`blind-panel-${i}`}
                      data-testid={`blind-toggle-${i}`}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span aria-hidden="true" style={{ color: '#f59e0b', flexShrink: 0 }}>
                        ⚠
                      </span>
                      <span
                        style={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, color: '#1f2937' }}
                      >
                        {b.title}
                      </span>
                      <span aria-hidden="true" style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                        {isOpen ? '▲' : '▼'}
                      </span>
                    </button>
                    {isOpen ? (
                      <div id={`blind-panel-${i}`} style={{ padding: '0 1rem 0.75rem 2rem' }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            lineHeight: 1.6,
                          }}
                        >
                          {b.desc}
                        </p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>

        <Card aria-label="Recommended actions">
          <CardBody style={{ display: 'grid', gap: '0.75rem' }}>
            <h2
              id="recommendations"
              style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: '#111827' }}
            >
              <span aria-hidden="true">📖 </span>
              Recommended actions
            </h2>
            <div style={{ display: 'grid', gap: '0.625rem' }}>
              {recommendations.map((r) => (
                <div
                  key={r.rank}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    background: '#f9fafb',
                    borderRadius: '0.5rem',
                    padding: '0.75rem',
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '1.75rem',
                      height: '1.75rem',
                      borderRadius: '9999px',
                      background: '#1f4ed8',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {r.rank}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#1f2937',
                        }}
                      >
                        {r.title}
                      </p>
                      <span
                        style={{
                          padding: '0.0625rem 0.375rem',
                          background: 'rgba(31, 78, 216, 0.1)',
                          color: '#1f4ed8',
                          borderRadius: '0.25rem',
                          fontSize: '0.625rem',
                          fontWeight: 500,
                        }}
                      >
                        {r.badge}
                      </span>
                    </div>
                    <p
                      style={{
                        margin: '0.25rem 0 0',
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        lineHeight: 1.6,
                      }}
                    >
                      {r.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Disclaimer. */}
      <div
        id="disclaimer"
        role="note"
        aria-label="Disclaimer"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '0.5rem',
          padding: '1rem',
        }}
      >
        <span aria-hidden="true" style={{ color: '#d97706', flexShrink: 0, fontSize: '1.25rem' }}>
          ⚠
        </span>
        <div>
          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#92400e' }}>
            Disclaimer
          </p>
          <p
            style={{
              margin: '0.25rem 0 0',
              fontSize: '0.875rem',
              color: '#b45309',
              lineHeight: 1.6,
            }}
          >
            The information on this page is general reference material. Each country’s import
            regulations and tax systems are revised frequently. For any actual import procedure,
            always confirm with the relevant country’s official authorities and a customs broker.
            Provide Cars accepts no liability for any damages arising from reliance on the
            information on this page.
          </p>
        </div>
      </div>
    </section>
  );
}
